/**
 * useCrmPhasePermissions - Hook de permissões granulares por fase de Negócio (CRM)
 *
 * Espelho arquitetural de `useTaskPhasePermissions`, escopado por **workspace**.
 *
 * Consome a RPC `get_crm_phase_permissions_for_user(p_workspace_id, p_tenant_hint)`
 * e expõe a função `canDoOnPhase(phaseId, action)` para guards de UI.
 *
 * Regras (resolvidas server-side; o hook apenas reflete):
 * - Workspace com `phase_permissions_enabled = false` → RPC retorna vazio
 *   → o frontend deve usar a lógica legada (`usePermissions` por menu_key).
 * - Admins (root_admin / tenant_admin) → RPC retorna linha sintética com tudo TRUE.
 * - Supervisor / vendedor sem perfil de permissão → RPC retorna vazio (deny by default).
 * - Supervisor / vendedor com perfil → segue a matriz da tabela `crm_phase_permissions`.
 *
 * @version 1.0.0
 * RELEASE: 2026-04-23 v1.0.0 - Criação inicial (espelho de useTaskPhasePermissions)
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { createLogger } from '@/lib/logger';

/** Logger do módulo */
const logger = createLogger('useCrmPhasePermissions');

/** Tipos de ação que podem ser verificadas por fase de Negócio */
export type CrmPhaseAction =
  | 'can_view'
  | 'can_create'
  | 'can_edit'
  | 'can_delete'
  | 'can_reopen'
  | 'can_change_phase'
  // v1.1.0 - Cancelar uma proposta comercial já ACEITA (ação de exceção)
  | 'can_cancel_accepted_proposal';

/** Registro de permissão por fase retornado pela RPC */
export interface CrmPhasePermissionRow {
  phase_id: string;
  phase_name: string;
  phase_color: string;
  pipeline_id: string;
  pipeline_name: string;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_reopen: boolean;
  can_change_phase: boolean;
  /** v1.1.0 - Cancelar proposta já aceita */
  can_cancel_accepted_proposal: boolean;
}

/** Interface do retorno do hook */
export interface CrmPhasePermissions {
  /** Verifica se o usuário pode realizar uma ação em uma fase específica do workspace */
  canDoOnPhase: (phaseId: string | null | undefined, action: CrmPhaseAction) => boolean;
  /** Retorna todas as permissões de uma fase específica */
  getPhasePermissions: (phaseId: string) => CrmPhasePermissionRow | undefined;
  /** Indica se as permissões granulares estão **ativas** para este workspace
   *  (ou seja, se o toggle está ON no workspace OU o usuário é admin).
   *  Quando false, o frontend deve usar a lógica legada (usePermissions). */
  isGranularEnabled: boolean;
  /** Flag indicando se as permissões estão carregando */
  isLoading: boolean;
  /** Se o usuário é admin (acesso total — bypass) */
  isAdmin: boolean;
  /** Mapa de permissões por fase (para iterações) */
  permissionsMap: Map<string, CrmPhasePermissionRow>;
}

/**
 * Hook central de permissões granulares por fase de Negócio.
 *
 * @param workspaceId - ID do workspace (obrigatório). Se nulo/undefined, hook retorna estado neutro.
 */
export function useCrmPhasePermissions(workspaceId?: string | null): CrmPhasePermissions {
  const { currentTenant } = useAuth();
  const { isAdmin, loading: permLoading } = usePermissions();

  // Busca permissões granulares via RPC (cache 60s)
  const { data: rawData, isLoading: isQueryLoading } = useQuery({
    queryKey: ['crm-phase-permissions', currentTenant?.id, workspaceId || 'none'],
    queryFn: async () => {
      if (!workspaceId || !currentTenant?.id) return [] as CrmPhasePermissionRow[];

      const { data, error } = await supabase.rpc('get_crm_phase_permissions_for_user', {
        p_workspace_id: workspaceId,
        p_tenant_hint: currentTenant.id,
      } as never);

      if (error) {
        logger.error('Erro ao buscar permissões CRM por fase', { error: error.message });
        throw error;
      }

      return (data || []) as CrmPhasePermissionRow[];
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    enabled: !permLoading && !!workspaceId && !!currentTenant?.id,
  });

  // Mapa indexado por phase_id para acesso O(1)
  const permissionsMap = useMemo(() => {
    const map = new Map<string, CrmPhasePermissionRow>();
    if (rawData) {
      for (const row of rawData) {
        map.set(row.phase_id, row);
      }
    }
    return map;
  }, [rawData]);

  // Granular ATIVO se: admin OU se a RPC retornou algo (toggle ON com perfil válido)
  const isGranularEnabled = useMemo(() => {
    if (isAdmin) return true;
    return (rawData?.length ?? 0) > 0;
  }, [isAdmin, rawData]);

  /**
   * Verifica se o usuário pode realizar uma ação em uma fase.
   * Quando o granular está OFF (RPC vazia e não-admin), retorna `true` por padrão
   * para preservar a UX legada — o controle nesse caso fica por conta do `usePermissions`
   * (camada de menu) ou da regra clássica vendedor/supervisor.
   */
  const canDoOnPhase = useCallback(
    (phaseId: string | null | undefined, action: CrmPhaseAction): boolean => {
      // Admin sempre pode tudo
      if (isAdmin) return true;

      // Granular OFF → não interferimos; UI deve usar lógica legada
      if (!isGranularEnabled) return true;

      // Carregando → bloqueia para evitar flash de conteúdo não autorizado
      if (isQueryLoading || permLoading) return false;

      // Sem phase_id quando granular ON → deny
      if (!phaseId) return false;

      const perm = permissionsMap.get(phaseId);
      if (!perm) return false;

      return perm[action] ?? false;
    },
    [isAdmin, isGranularEnabled, isQueryLoading, permLoading, permissionsMap]
  );

  /** Retorna registro completo de permissões da fase */
  const getPhasePermissions = useCallback(
    (phaseId: string): CrmPhasePermissionRow | undefined => permissionsMap.get(phaseId),
    [permissionsMap]
  );

  return {
    canDoOnPhase,
    getPhasePermissions,
    isGranularEnabled,
    isLoading: isQueryLoading || permLoading,
    isAdmin,
    permissionsMap,
  };
}
